import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ChevronDown } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function DriverForm() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", city: "", province: "Saskatchewan", vehicle_make: "", vehicle_model: "", vehicle_year: "", license_class: "", experience_years: 0, message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [docName, setDocName] = useState("");
  const provinces = ["Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador", "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan"];
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/drivers/apply`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      setSent(true);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };
  const handleDocUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setDocName(file.name);
    // For now, just show the filename - actual upload happens when backend supports it
    setUploading(false);
    e.target.value = "";
  };
  if (sent) return (
    <div data-testid="driver-app-success" className="text-center py-12">
      <CheckCircle size={48} className="text-[#E60000] mx-auto mb-4" />
      <h3 className="font-['Outfit'] text-xl font-bold text-white mb-2">Application Submitted!</h3>
      <p className="text-white/50 text-sm">We'll review and get back to you within 48 hours.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input data-testid="driver-name" required value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} placeholder="Full Name *" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
        <Input data-testid="driver-email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Email *" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Input data-testid="driver-phone" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone *" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
        <Input data-testid="driver-city" required value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="City *" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
        <select data-testid="driver-province" value={form.province} onChange={e => setForm({...form, province: e.target.value})} className="h-11 bg-white/5 border border-white/10 text-white text-sm px-3">{provinces.map(p => <option key={p} value={p}>{p}</option>)}</select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Input value={form.vehicle_make} onChange={e => setForm({...form, vehicle_make: e.target.value})} placeholder="Vehicle Make" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
        <Input value={form.vehicle_model} onChange={e => setForm({...form, vehicle_model: e.target.value})} placeholder="Vehicle Model" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
        <Input value={form.vehicle_year} onChange={e => setForm({...form, vehicle_year: e.target.value})} placeholder="Year" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input value={form.license_class} onChange={e => setForm({...form, license_class: e.target.value})} placeholder="License Class (e.g. Class 5)" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
        <div>
          <label className="flex items-center gap-2 h-11 px-3 bg-white/5 border border-white/10 text-white/50 text-sm cursor-pointer hover:border-[#E60000]/30 transition-colors">
            {uploading ? "Uploading..." : docName ? `${docName}` : "Upload License/Insurance (optional)"}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleDocUpload} />
          </label>
        </div>
      </div>
      {error && <p className="text-[#E60000] text-sm">{error}</p>}
      <Button type="submit" data-testid="driver-submit" disabled={loading} className="w-full bg-[#E60000] hover:bg-[#FF3B30] text-white h-12 font-semibold">{loading ? "Submitting..." : "Submit Application"}</Button>
    </form>
  );
}

export function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", inquiry_type: "general", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await fetch(`${API}/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error("Failed to send");
      setSent(true);
    } catch (err) { console.error("Contact submit failed:", err); } finally { setLoading(false); }
  };
  if (sent) return (
    <div data-testid="contact-success" className="text-center py-8">
      <CheckCircle size={40} className="text-[#E60000] mx-auto mb-3" />
      <p className="text-white font-semibold">Message Sent!</p>
      <p className="text-white/50 text-sm mt-1">We'll get back to you soon.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input data-testid="contact-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Name *" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
        <Input data-testid="contact-email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Email *" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
      </div>
      <select data-testid="contact-inquiry-type" value={form.inquiry_type} onChange={e => setForm({...form, inquiry_type: e.target.value})} className="w-full h-11 bg-white/5 border border-white/10 text-white text-sm px-3">
        <option value="general">General Inquiry</option><option value="rider">Rider Support</option><option value="driver">Driver Support</option><option value="business">Business Partnership</option>
      </select>
      <Textarea data-testid="contact-message" required value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Your message *" rows={4} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none" />
      <Button type="submit" data-testid="contact-submit" disabled={loading} className="w-full bg-[#E60000] hover:bg-[#FF3B30] text-white h-11 font-semibold">{loading ? "Sending..." : "Send Message"}</Button>
    </form>
  );
}

export function FAQSection() {
  const [faqs, setFaqs] = useState([]);
  const [openIdx, setOpenIdx] = useState(0);
  useEffect(() => { fetch(`${API}/cms/faqs/list`).then(r => r.json()).then(setFaqs).catch(() => {}); }, []);
  return (
    <div className="space-y-2">
      {faqs.slice(0, 6).map((f, i) => (
        <div key={f.id || i} className="border border-white/5 bg-black/20 backdrop-blur-sm">
          <button data-testid={`faq-toggle-${i}`} onClick={() => setOpenIdx(openIdx === i ? -1 : i)} className="w-full flex items-center justify-between p-4 text-left">
            <span className="text-white text-sm font-medium pr-4">{f.question}</span>
            <ChevronDown size={16} className={`text-white/40 shrink-0 transition-transform ${openIdx === i ? "rotate-180 text-[#E60000]" : ""}`} />
          </button>
          <AnimatePresence>
            {openIdx === i && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                <p className="px-4 pb-4 text-white/50 text-sm leading-relaxed">{f.answer}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}
